import { useState } from "react";

import { useBlocker } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";

import { ConfirmationModal } from "@components/ConfirmationModal";
import { FloatingPanel } from "@components/FloatingPanel";
import { MapToReportBuilderDialog } from "@components/MapToReportBuilderDialog";
import { caseNumberFromPath } from "@components/RecordingDock/dockVisibility";
import { useRecordingDock } from "@components/RecordingDock/store/useRecordingDock";
import { Button } from "@components/ui/button";

import { useCopyToClipboard } from "@hooks/use-copy-to-clipboard";
import { useSelectedTextWithin } from "@hooks/useSelectedTextWithin";

import { CaseSummaryBody } from "./CaseSummaryBody";
import {
  CASE_SUMMARY_PLACEHOLDER,
  CASE_SUMMARY_SOURCE_NAME,
  CASE_SUMMARY_TITLE,
} from "./constants";

type CaseSummaryPanelProps = {
  caseNumber: string;
  templateId?: string;
  /** The document's sub-title: subject · matter type · date · recipient. */
  documentSubtitle?: string;
  onClose: () => void;
};

/**
 * The open case summary (MDRS-486). Mounted only while open, so its
 * selection, mapping and copy state start fresh on every opening.
 *
 * Mapping reuses the Prep summary-highlight values, so the backend capability
 * check and Report Builder provenance need no change.
 */
export const CaseSummaryPanel = ({
  caseNumber,
  templateId,
  documentSubtitle,
  onClose,
}: CaseSummaryPanelProps) => {
  // State, not a ref: the body remounts when the panel pops out, and the
  // selection hook has to follow it into the other window.
  const [body, setBody] = useState<HTMLElement | null>(null);
  const { hasSelection, getSelectedText, clearSelection } =
    useSelectedTextWithin(body);
  const { copied, failed, copy } = useCopyToClipboard();
  const [mapText, setMapText] = useState<string | null>(null);

  // Leaving the case closes the summary and any separate window, so confirm
  // first, as a live Assessment Scribe recording does. Moving between the
  // case's own pages keeps it open. While this case is recording, the
  // recording's own prompt already covers leaving, so don't ask twice.
  // Signing out has already been chosen, so it isn't asked about either.
  const leaveCase = useBlocker({
    shouldBlockFn: ({ next }) =>
      next.pathname !== "/signing-out" &&
      caseNumberFromPath(next.pathname) !== caseNumber &&
      !useRecordingDock.getState().ownsCase(caseNumber),
    withResolver: true,
    // Nothing is lost by closing or reloading the tab: no browser prompt.
    enableBeforeUnload: false,
  });

  const handleCopy = () =>
    copy(body?.innerText || body?.textContent || "", {
      // The clipboard of the window the panel is showing in.
      clipboard: body?.ownerDocument.defaultView?.navigator.clipboard,
    });

  const handleMap = () => {
    const text = getSelectedText();
    if (!text) return;
    setMapText(text);
    clearSelection();
  };

  const footer = (
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs text-foreground-muted">
        Generated from this case&apos;s records.
      </p>
      {/* The primary action, Map, sits right-most. */}
      <div className="flex items-center gap-2">
        <Button variant="simple-outline" size="sm" onClick={handleCopy}>
          {copied ? "Copied" : failed ? "Couldn't copy" : "Copy"}
        </Button>
        {hasSelection && (
          <Button
            size="sm"
            // Keep the selection (and so this button) through the press.
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleMap}
          >
            Map to Report Builder
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <FloatingPanel
        onClose={onClose}
        title={CASE_SUMMARY_TITLE}
        subtitle={CASE_SUMMARY_PLACEHOLDER.subtitle}
        icon={<Sparkles />}
        footer={footer}
      >
        <CaseSummaryBody containerRef={setBody} subtitle={documentSubtitle} />
        {/* Rendered inside the panel so that, when popped out, the dialog
            portals into the separate window rather than back into this one. */}
        <MapToReportBuilderDialog
          open={!!mapText}
          onOpenChange={(open) => !open && setMapText(null)}
          text={mapText ?? ""}
          targetField="summary_from_doc_sorter"
          extractType="summary_highlight"
          caseNumber={caseNumber}
          templateId={templateId}
          sourceFileName={CASE_SUMMARY_SOURCE_NAME}
        />
      </FloatingPanel>

      {/* Outside the panel, so it shows in this window, where the navigation
          started, even while the summary is popped out. */}
      <ConfirmationModal
        open={leaveCase.status === "blocked"}
        // Dismissing (Esc / overlay) is the safe choice: stay.
        onOpenChange={(open) => !open && leaveCase.reset?.()}
        icon={<Sparkles className="size-10 text-primary" strokeWidth={2} />}
        title="Documentation summary still open"
        description="Leaving this case will close the documentation summary. You can reopen it from the case detail page."
        confirmButtonText="Close summary and leave"
        cancelButtonText="Stay on this case"
        onConfirm={() => leaveCase.proceed?.()}
        onCancel={() => leaveCase.reset?.()}
      />
    </>
  );
};
